import { projectAttributionContext, type AttributionContext } from './contract-v2';
import {
  createContextId,
  adoptNavigationContext,
  currentAccountScope,
  immutableContext,
  reportContextDiagnostic,
  sameAccount,
  type AccountScope,
  type ContextDiagnostic,
  type ScopedContext,
} from './context';

export const NAVIGATION_HANDOFF_PARAM = 'analytics_handoff';
export const NAVIGATION_HANDOFF_PREFIX = 'aydentify:analytics-handoff:v2:';
export const NAVIGATION_HANDOFF_TTL_MS = 5 * 60 * 1000;
export const NAVIGATION_HANDOFF_MAX_RECORDS = 20;
export const NAVIGATION_HANDOFF_MAX_BYTES = 8 * 1024;

export type NavigationContext = ScopedContext<AttributionContext>;

export interface NavigationHandoffInput {
  readonly href: string;
  readonly context: AttributionContext;
}

interface HandoffRecord {
  version: 2;
  token: string;
  createdAt: number;
  expiresAt: number;
  account: AccountScope;
  destination: string;
  context: AttributionContext;
}

export interface HandoffLockManager {
  request<T>(name: string, options: { ifAvailable: true }, callback: (lock: unknown | null) => Promise<T> | T): Promise<T>;
}

export interface NavigationRuntime {
  account(): AccountScope | undefined;
  newId(): string | undefined;
  href(): string;
  storage(): Storage | undefined;
  locks(): HandoffLockManager | undefined;
  now?(): number;
  diagnostic?(reason: ContextDiagnostic): void;
  adopt?(context: NavigationContext): boolean;
}

function byteLength(value: string): number {
  // TextEncoder is not available in every supported test/older browser environment.
  return new Blob([value]).size;
}

function validToken(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9_-]{16,128}$/.test(value);
}

/** Remove only analytics transport state, leaving business query parameters and fragments intact. */
export function stripNavigationHandoff(href: string, base?: string): { href: string; token?: string } {
  try {
    const url = new URL(href, base || (typeof window !== 'undefined' ? window.location.href : 'https://local.invalid/'));
    const values = url.searchParams.getAll(NAVIGATION_HANDOFF_PARAM);
    url.searchParams.delete(NAVIGATION_HANDOFF_PARAM);
    const result = { href: url.href, token: values.length === 1 && validToken(values[0]) ? values[0] : undefined };
    return result;
  } catch {
    return { href };
  }
}

/** Token storage is shared across tabs; active workflow/search state is not. */
export class NavigationHandoffStore {
  constructor(private readonly runtime: NavigationRuntime) {}

  private diagnostic(reason: ContextDiagnostic): void {
    try { this.runtime.diagnostic?.(reason); } catch { /* no recursive telemetry */ }
  }

  private now(): number { return this.runtime.now?.() ?? Date.now(); }

  private clean(storage: Storage, reserve = 1): void {
    const records: { key: string; created: number }[] = [];
    // Take a key snapshot because removing an item shifts localStorage indexes.
    const keys = Array.from({ length: storage.length }, (_, index) => storage.key(index))
      .filter((key): key is string => !!key && key.startsWith(NAVIGATION_HANDOFF_PREFIX));
    for (const key of keys) {
      try {
        const raw = storage.getItem(key);
        if (!raw || byteLength(raw) > NAVIGATION_HANDOFF_MAX_BYTES) throw new Error('bounded handoff');
        const record = JSON.parse(raw) as HandoffRecord;
        if (record.version !== 2 || !Number.isFinite(record.expiresAt) || record.expiresAt <= this.now()) {
          storage.removeItem(key);
        } else records.push({ key, created: record.createdAt });
      } catch { storage.removeItem(key); }
    }
    records.sort((a, b) => a.created - b.created || a.key.localeCompare(b.key));
    while (records.length > NAVIGATION_HANDOFF_MAX_RECORDS - reserve) storage.removeItem(records.shift()!.key);
  }

  /** Synchronous so the caller can open the returned URL inside its accepted user gesture. */
  create(input: NavigationHandoffInput): { token: string; href: string } | undefined {
    try {
      const account = this.runtime.account();
      if (!account) { this.diagnostic('missing_context'); return undefined; }
      const sourceScope = (input.context as Partial<NavigationContext>).accountScope;
      if (sourceScope && !sameAccount(sourceScope, account)) {
        this.diagnostic('handoff_rejected'); return undefined;
      }
      const current = new URL(this.runtime.href());
      const clean = stripNavigationHandoff(input.href, current.href);
      const destination = new URL(clean.href);
      if (destination.origin !== current.origin || !['http:', 'https:'].includes(destination.protocol)) {
        this.diagnostic('handoff_rejected'); return undefined;
      }
      const context = projectAttributionContext(input.context);
      if (!context.valid) { this.diagnostic('schema_rejected'); return undefined; }
      const token = this.runtime.newId();
      if (!validToken(token)) { this.diagnostic('handoff_unavailable'); return undefined; }
      const storage = this.runtime.storage();
      if (!storage || !this.runtime.locks()) { this.diagnostic('handoff_unavailable'); return undefined; }
      const createdAt = this.now();
      const record: HandoffRecord = {
        version: 2,
        token,
        createdAt,
        expiresAt: createdAt + NAVIGATION_HANDOFF_TTL_MS,
        account: { ...account },
        destination: destination.href,
        context: context.value,
      };
      const serialized = JSON.stringify(record);
      if (byteLength(serialized) > NAVIGATION_HANDOFF_MAX_BYTES) {
        this.diagnostic('schema_rejected'); return undefined;
      }
      this.clean(storage);
      storage.setItem(`${NAVIGATION_HANDOFF_PREFIX}${token}`, serialized);
      // Another tab can create between the preflight cleanup and this synchronous write.
      this.clean(storage, 0);
      destination.searchParams.set(NAVIGATION_HANDOFF_PARAM, token);
      return { token, href: destination.href };
    } catch {
      this.diagnostic('handoff_unavailable');
      return undefined;
    }
  }

  /** Consume only after currentUser verifies the account. Locking makes duplicate tabs race safely. */
  async consume(token: string, expected?: AccountScope): Promise<NavigationContext | undefined> {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let expired = false;
    try {
      if (!validToken(token)) { this.diagnostic('handoff_rejected'); return undefined; }
      const account = this.runtime.account();
      if (!account || (expected && !sameAccount(expected, account))) {
        this.diagnostic('handoff_rejected'); return undefined;
      }
      const storage = this.runtime.storage();
      const locks = this.runtime.locks();
      if (!storage || !locks) { this.diagnostic('handoff_unavailable'); return undefined; }
      const acquisition = locks.request(`${NAVIGATION_HANDOFF_PREFIX}${token}`, { ifAvailable: true }, (lock) => {
        if (!lock || expired) { this.diagnostic('handoff_unavailable'); return undefined; }
        // An account switch may happen while another tab holds the lock.
        if (!sameAccount(account, this.runtime.account())) {
          this.diagnostic('handoff_rejected'); return undefined;
        }
        const key = `${NAVIGATION_HANDOFF_PREFIX}${token}`;
        const serialized = storage.getItem(key);
        if (!serialized) { this.diagnostic('handoff_rejected'); return undefined; }
        if (byteLength(serialized) > NAVIGATION_HANDOFF_MAX_BYTES) {
          storage.removeItem(key); this.diagnostic('handoff_rejected'); return undefined;
        }
        let record: HandoffRecord;
        try { record = JSON.parse(serialized); } catch {
          storage.removeItem(key); this.diagnostic('handoff_rejected'); return undefined;
        }
        const destination = stripNavigationHandoff(this.runtime.href()).href;
        if (record.version !== 2 || record.token !== token ||
          !Number.isFinite(record.createdAt) || record.createdAt > this.now() ||
          !Number.isFinite(record.expiresAt) || record.expiresAt <= this.now() ||
          record.expiresAt - record.createdAt !== NAVIGATION_HANDOFF_TTL_MS ||
          !sameAccount(record.account, account) || record.destination !== destination) {
          storage.removeItem(key); this.diagnostic('handoff_rejected'); return undefined;
        }
        const projected = projectAttributionContext(record.context);
        if (!projected.valid) {
          storage.removeItem(key); this.diagnostic('handoff_rejected'); return undefined;
        }
        // Deletion is inside the cross-tab lock and precedes returning any context.
        storage.removeItem(key);
        const { page_view_id: _sourceView, ...attribution } = projected.value;
        const accepted = immutableContext({ ...attribution, accountScope: { ...account } });
        if (this.runtime.adopt && !this.runtime.adopt(accepted)) {
          this.diagnostic('handoff_rejected'); return undefined;
        }
        return accepted;
      });
      return await Promise.race([
        acquisition,
        new Promise<undefined>((resolve) => {
          timeout = setTimeout(() => {
            expired = true;
            this.diagnostic('handoff_unavailable');
            resolve(undefined);
          }, 250);
        }),
      ]);
    } catch {
      this.diagnostic('handoff_unavailable');
      return undefined;
    } finally {
      if (timeout) clearTimeout(timeout);
    }
  }
}

const handoffs = new NavigationHandoffStore({
  account: currentAccountScope,
  newId: createContextId,
  href: () => window.location.href,
  storage: () => window.localStorage,
  locks: () => typeof navigator !== 'undefined' ? (navigator as any).locks : undefined,
  diagnostic: reportContextDiagnostic,
  adopt: adoptNavigationContext,
});

export const createNavigationHandoff = (input: NavigationHandoffInput) => handoffs.create(input);
export const consumeNavigationHandoff = (token: string, expected?: AccountScope) => handoffs.consume(token, expected);

/** Read and remove the transport token before the observer or SDK can see a page URL. */
export function takeNavigationHandoffToken(): string | undefined {
  try {
    const current = window.location.href;
    const stripped = stripNavigationHandoff(current);
    if (stripped.href !== current) window.history.replaceState(window.history.state, '', stripped.href);
    if (stripped.href !== current && !stripped.token) reportContextDiagnostic('handoff_rejected');
    return stripped.token;
  } catch {
    reportContextDiagnostic('handoff_unavailable');
    return undefined;
  }
}
